import { removeAccents } from '@utils/Format'

/**
 * Índice da busca global (command palette).
 *
 * Constrói entradas pesquisáveis a partir do `meta.search` declarado nas rotas
 * (fonte única de verdade: router). Cada rota pode declarar:
 *
 *   meta: {
 *     search: {
 *       title: 'Comissões',                    // obrigatório — nome exibido
 *       description: 'Regras e fechamentos',   // opcional
 *       keywords: ['repasse', 'fechamento'],   // opcional — sinônimos
 *       icon: 'coin',                          // opcional — nome do icon-svg
 *       group: 'Gestão de escritórios',        // opcional — seção da navbar
 *       landing: 'compliance.overview',        // opcional — destino no lugar
 *                                              // da raiz (só p/ quem acessa)
 *       meta: { permission: ['gestor'] },      // opcional — gate de DESCOBERTA (ver abaixo)
 *       actions: [                             // opcional — ações rápidas
 *         {
 *           id: 'novo-cliente',
 *           title: 'Cadastrar cliente',
 *           modal: MODAL.CADASTRAR_CLIENTES,   // abre o modal direto, sem navegar
 *           modalPayload: { item: { new: true } },
 *           meta: { permission: ['gestor'] },  // opcional — gate só desta ação
 *         },
 *       ],
 *     },
 *   }
 *
 * `to` e `modal` NÃO são mutuamente exclusivos — `runAction` (consumidor em
 * global-search.vue) compõe os dois quando ambos estão presentes: navega
 * primeiro (aguardando a navegação resolver) e só então emite `modal`,
 * abortando o emit se o `$route.name` final divergir do `to.name` pedido
 * (guard/redirect interceptou). A maioria das ações do CRM hoje declara só
 * `modal` (abre em cima da tela atual, sem navegar); `to`+`modal` juntos
 * continuam servindo ações que navegam e abrem modal na mesma seleção (ex.:
 * "Agendar tarefa"). `modal` também pode apontar para um canal GlobalBus que
 * NÃO abre modal nenhum — só coordena estado entre a busca e a view (ex.:
 * `SEARCH_SIGNAL.CRM_TRANSCRICOES_MODO_EU`, que ativa um filtro).
 *
 * O acesso é herdado do próprio meta da rota (can_show / only_aawz /
 * whitelabel_only / permission / customPermission), então rota invisível pro
 * perfil nunca aparece na busca.
 *
 * `search.meta` / `action.meta`: gate OPCIONAL por entrada, avaliado com o
 * mesmo `canAccessRouteMeta` usado pelo guard de rota (ex.:
 * `{ permission: ['gestor'] }`). É um gate de DESCOBERTA, não de ACESSO — a
 * rota/ação segue alcançável pelo fluxo normal (URL, navegação) para quem não
 * a vê no índice; ele só decide se a entrada aparece no Ctrl/Cmd+K. Ausente
 * (`undefined`) = sempre indexado, igual ao comportamento anterior a este
 * campo (retrocompat).
 */

export const ENTRY_TYPE_PAGE = 'page'
export const ENTRY_TYPE_ACTION = 'action'

const SCORE_TITLE_STARTS_WITH = 3
const SCORE_TITLE_INCLUDES = 2
const SCORE_KEYWORD_INCLUDES = 1.5
const SCORE_DESCRIPTION_INCLUDES = 1

const PERMISSION_ALL = 'all'
const PERMISSION_PROFISSIONAL = 'profissional'
const PERMISSION_CLIENTE = 'cliente'
const PERMISSION_GESTOR = 'gestor'

export const normalizeSearchText = (text) => {
  return removeAccents(String(text || '').toLowerCase()).trim()
}

const matchesPermissionProfile = (permission, context) => {
  if (!Array.isArray(permission) || permission.includes(PERMISSION_ALL)) {
    return true
  }
  if (permission.includes(PERMISSION_PROFISSIONAL) && !context.isCliente) {
    return true
  }
  if (permission.includes(PERMISSION_CLIENTE) && context.isCliente) {
    return true
  }
  return permission.includes(PERMISSION_GESTOR) && context.isGestor
}

// Espelha o guard: meta.customPermission({ user, show }) decide o acesso.
// Fail-closed: erro na função de permissão esconde a rota da busca.
const passesCustomPermission = (routeMeta, context) => {
  if (typeof routeMeta.customPermission !== 'function') {
    return true
  }
  try {
    return !!routeMeta.customPermission({
      user: context.user || {},
      show: context.show || {},
    })
  } catch (err) {
    // Permissão custom quebrada → fail-closed (esconde a rota da busca).
    console.debug('global-search: customPermission lançou', err)
    return false
  }
}

// Espelha o guard: listCanShow libera se QUALQUER feature da lista está ativa.
const passesListCanShow = (routeMeta, context) => {
  if (!Array.isArray(routeMeta.listCanShow)) {
    return true
  }
  const show = context.show || {}
  return routeMeta.listCanShow.some((feature) => !!show[feature])
}

// Espelha o guard: only_aawz não é "só interno" — um escritório externo com a
// external_flag da rota ativa também acessa. Sem isso a rota some da busca de
// quem tem acesso (mesma classe de bug do can_show em array).
// `internal` puro não tem escape por flag; só `only_aawz` tem.
const passesInternalOnly = (routeMeta, context) => {
  if (!routeMeta.only_aawz && !routeMeta.internal) {
    return true
  }
  if (context.isInternal) {
    return true
  }
  const show = context.show || {}
  return !!(
    routeMeta.only_aawz &&
    routeMeta.external_flag &&
    show[routeMeta.external_flag]
  )
}

/**
 * Replica os filtros de visibilidade do router guard / navbar para uma rota.
 * context = { canShow(feature), user, show, isInternal, isCliente, isGestor,
 * isWhitelabel } — user e show alimentam customPermission/listCanShow, iguais
 * ao guard de src/router/index.js.
 */
// Espelha o guard: can_show pode ser uma feature ou um array de features, e o
// guard exige TODAS (`every`). Sem o unwrap, um array vira a chave "a,b" no
// getter e devolve undefined — escondendo a rota até de quem tem acesso.
const passesCanShow = (routeMeta, context) => {
  if (!routeMeta.can_show) {
    return true
  }
  const features = Array.isArray(routeMeta.can_show)
    ? routeMeta.can_show
    : [routeMeta.can_show]
  return features.every((feature) => !!context.canShow(feature))
}

// Espelha o guard: requireGestor (src/modules/tarefas/routes.js) checa
// user/currentUser IS_GESTOR num beforeEnter imperativo, que o índice não
// enxerga. Sem este gate, não-gestor veria a rota na busca e seria bloqueado
// só ao clicar. Opt-in: sem `meta.gestor_only` a rota não é restrita a gestores.
const passesGestorOnly = (routeMeta, context) => {
  if (!routeMeta.gestor_only) {
    return true
  }
  return !!context.isGestor
}

export const canAccessRouteMeta = (meta, context) => {
  const routeMeta = meta || {}
  if (!passesCanShow(routeMeta, context)) {
    return false
  }
  if (!passesInternalOnly(routeMeta, context)) {
    return false
  }
  if (routeMeta.whitelabel_only && !context.isWhitelabel) {
    return false
  }
  if (!passesCustomPermission(routeMeta, context)) {
    return false
  }
  if (!passesListCanShow(routeMeta, context)) {
    return false
  }
  if (!passesGestorOnly(routeMeta, context)) {
    return false
  }
  return matchesPermissionProfile(routeMeta.permission, context)
}

// Gate OPCIONAL por entrada (página/ação) no índice — DESCOBERTA, não ACESSO.
// Ausente = sempre permitido (retrocompat). Reusa canAccessRouteMeta pois o
// formato de entryMeta espelha o meta da rota (ex.: { permission: ['gestor'] }).
const entryAllowed = (entryMeta, context) =>
  !entryMeta || canAccessRouteMeta(entryMeta, context)

// Mapa name → meta de TODA a árvore de rotas — alimenta a resolução de
// `meta.search.landing`, cujo alvo é outro nó (em geral um filho da raiz).
const collectRouteMetaByName = (routes, metaByName) => {
  const routeList = routes || []
  routeList.forEach((route) => {
    if (route.name) {
      metaByName[route.name] = route.meta || {}
    }
    collectRouteMetaByName(route.children, metaByName)
  })
  return metaByName
}

// `landing` troca o destino da entrada (ex.: raiz do módulo → "Visão Geral")
// SÓ para quem acessa o alvo; os demais seguem para a raiz, cujo redirect leva
// ao Bem-vindo — a própria página de upsell. Basta o meta do PRÓPRIO alvo: ele
// é filho da raiz, que já passou pelo gate para a entrada existir (§3.4 do
// CLAUDE.md do módulo). Alvo inexistente na árvore → sem `to`.
const resolveLandingTo = (search, context, metaByName) => {
  const landing = search.landing
  const hasTarget = !!landing && Object.hasOwn(metaByName, landing)
  if (!hasTarget || !canAccessRouteMeta(metaByName[landing], context)) {
    return null
  }
  return { name: landing }
}

// A `key` segue o nome da rota raiz mesmo com `landing` — os recentes
// persistidos ({ key }) continuam resolvendo contra o índice.
const buildPageEntry = (route, search, landingTo) => {
  const entry = {
    type: ENTRY_TYPE_PAGE,
    key: `${ENTRY_TYPE_PAGE}:${route.name}`,
    routeName: route.name,
    title: search.title,
    description: search.description || '',
    keywords: search.keywords || [],
    icon: search.icon || '',
    group: search.group || '',
  }
  if (landingTo) {
    entry.to = landingTo
  }
  return entry
}

const buildActionEntry = (route, search, action) => {
  return {
    type: ENTRY_TYPE_ACTION,
    key: `${ENTRY_TYPE_ACTION}:${route.name}:${action.id}`,
    routeName: route.name,
    title: action.title,
    description: action.description || search.title,
    keywords: action.keywords || [],
    icon: action.icon || search.icon || '',
    group: search.group || '',
    to: action.to || null,
    modal: action.modal || '',
    modalPayload: action.modalPayload,
  }
}

// allowTitle: gate opcional por título (allowlist do sidebar). Quando ausente,
// indexa tudo (comportamento padrão). A recursão nos filhos NÃO é gateada — só
// a emissão da entrada — para alcançar rotas mantidas que são filhas na árvore.
const collectRouteEntries = (route, context, entries, options) => {
  const { allowTitle, metaByName } = options
  const meta = route.meta || {}
  const search = meta.search
  const accessible = canAccessRouteMeta(meta, context)
  const inAllowlist = !allowTitle || (search && allowTitle(search.title))
  const pageAllowed = entryAllowed(search && search.meta, context)
  const shouldEmitPage =
    search &&
    search.title &&
    route.name &&
    accessible &&
    inAllowlist &&
    pageAllowed
  if (shouldEmitPage) {
    entries.push(
      buildPageEntry(route, search, resolveLandingTo(search, context, metaByName))
    )
    const actions = search.actions || []
    actions.forEach((action) => {
      if (!entryAllowed(action.meta, context)) return
      entries.push(buildActionEntry(route, search, action))
    })
  }
  const children = route.children || []
  if (accessible) {
    children.forEach((child) =>
      collectRouteEntries(child, context, entries, options)
    )
  }
}

/**
 * Varre a árvore de rotas (router.options.routes) e devolve as entradas
 * pesquisáveis acessíveis ao perfil atual.
 * options.allowTitle(title) -> boolean: gate opcional de allowlist por título.
 */
export const buildSearchIndex = (routes, context, options) => {
  const opts = options || {}
  const allowTitle =
    typeof opts.allowTitle === 'function' ? opts.allowTitle : null
  const entries = []
  const routeList = routes || []
  const metaByName = collectRouteMetaByName(routeList, {})
  routeList.forEach((route) =>
    collectRouteEntries(route, context, entries, { allowTitle, metaByName })
  )
  return entries
}

const scoreEntry = (entry, query) => {
  const title = normalizeSearchText(entry.title)
  if (title.startsWith(query)) {
    return SCORE_TITLE_STARTS_WITH
  }
  if (title.includes(query)) {
    return SCORE_TITLE_INCLUDES
  }
  const keywords = entry.keywords || []
  const hasKeyword = keywords.some((keyword) =>
    normalizeSearchText(keyword).includes(query)
  )
  if (hasKeyword) {
    return SCORE_KEYWORD_INCLUDES
  }
  if (normalizeSearchText(entry.description).includes(query)) {
    return SCORE_DESCRIPTION_INCLUDES
  }
  return 0
}

/**
 * Filtra e ranqueia entradas do índice pela query digitada.
 */
export const filterSearchEntries = (entries, query, { limit = 8 } = {}) => {
  const normalizedQuery = normalizeSearchText(query)
  if (!normalizedQuery) {
    return []
  }
  return (entries || [])
    .map((entry) => ({ entry, score: scoreEntry(entry, normalizedQuery) }))
    .filter((scored) => scored.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score
      }
      return String(a.entry.title).localeCompare(String(b.entry.title))
    })
    .slice(0, limit)
    .map((scored) => scored.entry)
}
